import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Wand2, Loader2, CheckCircle2, Info } from "lucide-react";

/**
 * DeductionOptimiser — one-click deduction pre-fill widget.
 *
 * Opens a dialog listing scanned suggestions from the user's Investments +
 * Expenses. Each row has a checkbox; hitting "Add selected" bulk-creates
 * tax_proofs via `POST /api/tax/optimiser/accept`.
 *
 * Suggestions already imported once (matched by `source_id`) show as
 * "Already recorded" and are un-checkable.
 */
export default function DeductionOptimiser({ fy, onAccepted, testid = "tax-optimiser" }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [saving, setSaving] = useState(false);

    const load = () => {
        if (!fy) return;
        setLoading(true);
        api.get("/tax/optimiser", { params: { fy } })
            .then((r) => {
                setData(r.data);
                // Pre-check every new (not-already-recorded) suggestion
                setSelected(new Set(r.data.suggestions.filter((s) => !s.already_recorded).map((s) => s.id)));
            })
            .catch(() => toast.error("Failed to scan"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open) load();
    }, [open, fy]);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    const submit = async () => {
        if (selected.size === 0) { toast.error("Select at least one suggestion"); return; }
        setSaving(true);
        try {
            const res = await api.post("/tax/optimiser/accept", {
                suggestion_ids: Array.from(selected),
                financial_year: fy,
            });
            toast.success(`Added ${res.data.created} deduction${res.data.created === 1 ? "" : "s"}${res.data.skipped ? ` · ${res.data.skipped} skipped` : ""}`);
            setOpen(false);
            onAccepted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-stone-900 bg-stone-900 text-white text-xs font-medium hover:bg-stone-800 transition-colors"
                data-testid={`${testid}-open`}
            >
                <Wand2 size={12} /> Scan for deductions
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles size={16} className="text-amber-500" /> Deduction Optimiser
                        </DialogTitle>
                        <DialogDescription>
                            We scanned your Investments and Expenses for FY {fy} and matched them against Section 80C / 80D / 24(b) / 80E / 80G / HRA patterns.
                            Un-check anything you don&apos;t want; hit <em>Add selected</em> to save them as deduction proofs.
                        </DialogDescription>
                    </DialogHeader>

                    {loading && <div className="p-6 text-stone-500 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Scanning…</div>}

                    {!loading && data && (
                        <>
                            <SummaryBar data={data} selectedCount={selected.size} />
                            <div className="max-h-[420px] overflow-y-auto -mx-6 px-6 divide-y divide-stone-100">
                                {data.suggestions.length === 0 && (
                                    <div className="p-6 text-center text-sm text-stone-500" data-testid={`${testid}-empty`}>
                                        Nothing matched this FY. Add investments (PPF / EPF / NPS / ELSS) or tag expenses
                                        with categories like &quot;Health insurance&quot;, &quot;LIC premium&quot;, &quot;Home loan EMI&quot; and re-scan.
                                    </div>
                                )}
                                {Object.entries(data.by_section || {}).map(([section, rows]) => (
                                    <SectionGroup
                                        key={section}
                                        section={section}
                                        rows={rows}
                                        selected={selected}
                                        onToggle={toggle}
                                        testid={testid}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            className="bg-stone-900 hover:bg-stone-800 text-white gap-1.5"
                            onClick={submit}
                            disabled={saving || selected.size === 0}
                            data-testid={`${testid}-accept`}
                        >
                            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                            Add {selected.size || 0} selected
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function SummaryBar({ data, selectedCount }) {
    return (
        <div className="flex items-center gap-3 p-3 rounded-md bg-stone-50 border border-stone-200 text-xs" data-testid="tax-optimiser-summary">
            <Info size={14} className="text-stone-500" />
            <span>
                <strong>{data.new_suggestions}</strong> new suggestion{data.new_suggestions === 1 ? "" : "s"}
                {data.total_suggestions - data.new_suggestions > 0 && (
                    <span className="text-stone-500"> ({data.total_suggestions - data.new_suggestions} already recorded)</span>
                )}
                {" · "}
                Potential total <span className="font-mono-figure font-medium text-stone-800">{fmtMoney(data.total_amount_pending)}</span>
                {" · "}
                Selected <span className="font-medium">{selectedCount}</span>
            </span>
        </div>
    );
}

function SectionGroup({ section, rows, selected, onToggle, testid }) {
    return (
        <div className="py-3">
            <div className="eyebrow mb-2">{section} · {rows.length} row{rows.length === 1 ? "" : "s"}</div>
            <div className="space-y-2">
                {rows.map((s) => <Row key={s.id} s={s} checked={selected.has(s.id)} onToggle={() => onToggle(s.id)} testid={testid} />)}
            </div>
        </div>
    );
}

function Row({ s, checked, onToggle, testid }) {
    const conf = {
        high: "text-emerald-700 border-emerald-200 bg-emerald-50",
        medium: "text-blue-700 border-blue-200 bg-blue-50",
        low: "text-stone-600 border-stone-200 bg-stone-50",
    }[s.confidence] || "text-stone-600 border-stone-200 bg-stone-50";

    return (
        <label className={`flex items-start gap-3 p-2.5 rounded-md border ${s.already_recorded ? "border-dashed border-stone-200 opacity-60" : "border-stone-200 hover:border-stone-300"} cursor-pointer`} data-testid={`${testid}-row-${s.id}`}>
            <Checkbox
                checked={checked}
                disabled={s.already_recorded}
                onCheckedChange={onToggle}
                data-testid={`${testid}-check-${s.id}`}
                className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium">{s.source_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 border rounded font-medium tracking-widest uppercase ${conf}`}>
                        {s.confidence}
                    </span>
                    {s.sub_category && (
                        <span className="text-[10px] px-1.5 py-0.5 border border-stone-200 bg-white rounded text-stone-600 tracking-widest uppercase">
                            {s.sub_category}
                        </span>
                    )}
                    {s.already_recorded && (
                        <span className="text-[10px] px-1.5 py-0.5 border border-stone-300 bg-stone-100 rounded text-stone-500 tracking-widest uppercase">
                            Already recorded
                        </span>
                    )}
                    {s.llm && !s.already_recorded && (
                        <span className="text-[10px] px-1.5 py-0.5 border border-purple-200 bg-purple-50 rounded text-purple-800 tracking-widest uppercase font-medium" title="Classified by AI">
                            AI
                        </span>
                    )}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">{s.description}</div>
                <div className="text-[10.5px] text-stone-400 mt-1">{s.reason} · Date {fmtDate(s.suggested_date)}</div>
            </div>
            <div className="text-right shrink-0">
                <div className="text-sm font-medium tabular-nums">{fmtMoney(s.suggested_amount)}</div>
                <div className="text-[10px] text-stone-500 uppercase tracking-widest">{s.source === "investment" ? "Investment" : "Expenses"}</div>
            </div>
        </label>
    );
}
